import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, eur, fmtDate } from "../lib/api";
import { FileText, Receipt, Sparkles, ArrowRight, TrendingUp, Clock, CheckCircle2, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const StatCard = ({ icon: Icon, label, value, accent = "cyan", testid }) => (
  <div data-testid={testid} className="p-6 rounded-lg border border-white/10 bg-[#0A1128]/60 hover:-translate-y-1 transition-transform">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{label}</div>
      <Icon className={`w-4 h-4 ${accent === "orange" ? "text-brand-orange" : "text-brand-cyan"}`} strokeWidth={1.5} />
    </div>
    <div className="font-display font-bold text-3xl">{value}</div>
  </div>
);

const UsageBar = ({ label, current, max, testid }) => {
  const pct = Math.min(100, (current / max) * 100);
  const reached = current >= max;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-white/60">{label}</span>
        <span className={`font-mono ${reached ? "text-brand-orange font-bold" : "text-white/70"}`}>{current}/{max}</span>
      </div>
      <div className="h-1.5 bg-black/40 rounded overflow-hidden">
        <div className={`h-full ${reached ? "bg-brand-orange" : "bg-brand-cyan"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function Overview() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(() => setData({}));
    api.get("/usage").then(r => setUsage(r.data)).catch(() => setUsage(null));
  }, []);

  if (!data) return <div className="text-white/50">{t("common.loading")}</div>;
  const showUsage = usage && !usage.is_pro;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="overview-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">{t("nav.overview")}</div>
          <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight">{t("overview.title")}</h1>
        </div>
        <Link to="/app/presupuestos/nuevo">
          <Button data-testid="new-estimate-cta" className="bg-brand-orange hover:bg-brand-orange-hover text-white">
            <Sparkles className="w-4 h-4 mr-2" /> {t("overview.new_estimate_cta")}
          </Button>
        </Link>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} label={t("overview.stat_pending")} value={eur(data.pending_total || 0)} testid="stat-pending" />
        <StatCard icon={CheckCircle2} label={t("overview.stat_accepted")} value={eur(data.accepted_total || 0)} testid="stat-accepted" />
        <StatCard icon={Receipt} label={t("overview.stat_invoiced")} value={eur(data.invoiced_total || 0)} accent="orange" testid="stat-invoiced" />
        <StatCard icon={TrendingUp} label={t("overview.stat_paid")} value={eur(data.paid_total || 0)} accent="orange" testid="stat-paid" />
      </div>

      {showUsage && (
        <section className="p-6 rounded-lg border border-brand-orange/30 bg-gradient-to-br from-brand-orange/5 to-transparent" data-testid="usage-banner">
          <div className="flex justify-between items-start flex-wrap gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-orange mb-1">{t("overview.your_plan")}</div>
              <h3 className="font-display font-semibold">{t("overview.usage_month")}</h3>
            </div>
            <Link to="/app/planes">
              <Button data-testid="usage-upgrade-btn" size="sm" className="bg-brand-orange hover:bg-brand-orange-hover">
                <Crown className="w-3.5 h-3.5 mr-2" /> {t("overview.upgrade")}
              </Button>
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <UsageBar label={t("overview.usage_estimates")} current={usage.estimates_this_month} max={usage.limits.estimates_per_month} testid="usage-estimates" />
            <UsageBar label={t("overview.usage_invoices")} current={usage.invoices_this_month} max={usage.limits.invoices_per_month} testid="usage-invoices" />
            <UsageBar label={t("overview.usage_ai")} current={usage.ai_today} max={usage.limits.ai_per_day} testid="usage-ai" />
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="p-6 rounded-lg border border-white/10 bg-[#0A1128]/40">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-brand-cyan" strokeWidth={1.5} /> {t("overview.recent_estimates")}</h2>
            <Link to="/app/presupuestos" className="text-sm text-brand-cyan hover:underline flex items-center gap-1">{t("overview.see_all")} <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="space-y-2">
            {(data.recent_estimates || []).length === 0 && <div className="text-sm text-white/40 py-8 text-center">{t("overview.no_estimates")} <Link to="/app/presupuestos/nuevo" className="text-brand-cyan hover:underline">{t("overview.create_first")}</Link></div>}
            {(data.recent_estimates || []).map((e) => (
              <Link key={e.id} to={`/app/presupuestos/${e.id}`} className="flex justify-between items-center p-3 rounded hover:bg-white/5 transition-colors">
                <div>
                  <div className="font-mono text-xs text-white/50">{e.number}</div>
                  <div className="text-sm">{e.client_snapshot?.name || "Sin cliente"}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{eur(e.total || 0)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{e.status}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="p-6 rounded-lg border border-white/10 bg-[#0A1128]/40">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Receipt className="w-5 h-5 text-brand-orange" strokeWidth={1.5} /> {t("overview.recent_invoices")}</h2>
            <Link to="/app/facturas" className="text-sm text-brand-orange hover:underline flex items-center gap-1">{t("overview.see_all_invoices")} <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="space-y-2">
            {(data.recent_invoices || []).length === 0 && <div className="text-sm text-white/40 py-8 text-center">{t("overview.no_invoices")}</div>}
            {(data.recent_invoices || []).map((i) => (
              <Link key={i.id} to={`/app/facturas/${i.id}`} className="flex justify-between items-center p-3 rounded hover:bg-white/5 transition-colors">
                <div>
                  <div className="font-mono text-xs text-white/50">{i.number}</div>
                  <div className="text-sm">{i.client_snapshot?.name || "Sin cliente"}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{eur(i.total || 0)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{i.paid ? "cobrado" : i.status}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
